/**
 * Customer-facing API copy for /docs.
 *
 * Only the public `/v1` surface. Session routes, keys admin, Stripe, and the
 * dashboard console stay out of OpenAPI consumers and stay out of here.
 *
 * Keep query param lists in step with
 * `apps/api/src/services/platforms/ebay/standby.ts` (EbaySearchQuerySchema).
 */

export const PUBLIC_API_BASE = '/v1';

export type DocsNavItem = {
    to: string;
    label: string;
    /** Short line under the link on the overview. */
    blurb: string;
};

export const DOCS_NAV: DocsNavItem[] = [
    {
        to: '/docs',
        label: 'Overview',
        blurb: 'Auth, billing units, rate limits, error shapes.',
    },
    {
        to: '/docs/search',
        label: 'Search',
        blurb: 'One page of sold or active eBay listings.',
    },
];

export type ParamDoc = {
    name: string;
    type: string;
    required?: boolean;
    default?: string;
    description: string;
};

export const SEARCH_PARAMS: ParamDoc[] = [
    {
        name: 'keyword',
        type: 'string',
        required: true,
        description: 'What to search for. Same string a buyer would type on eBay.',
    },
    {
        name: 'sold',
        type: 'true | false',
        default: 'true',
        description: 'Completed sales, or currently listed items when false.',
    },
    {
        name: 'country',
        type: 'US | UK',
        default: 'US',
        description:
            'Marketplace pool. Host, residential proxy, and expected currency follow this code. US and UK only.',
    },
    {
        name: 'itemLocation',
        type: 'country | available | worldwide',
        default: 'worldwide',
        description:
            'Seller location relative to country. country = stock in that market; available = ships there; worldwide = no seller-location filter.',
    },
    {
        name: 'page',
        type: 'integer 1–100',
        default: '1',
        description: 'Which results page to fetch. Increment while hasNextPage is true.',
    },
    {
        name: 'count',
        type: 'integer 1–240',
        default: '240',
        description:
            'Max rows to return from this page after server-side filters. Accepted up to 240, but eBay caps a mobile results page at ~200 cards, so anything above 200 returns whatever the page held (measured 2026-08-01 across ten keywords). Treat 200 as the real ceiling.',
    },
    {
        name: 'daysToScrape',
        type: 'integer 0–365',
        default: '0',
        description:
            'Drop sold rows older than this many days. 0 disables. Filters the page. eBay has no sold-date sort, so a narrow window can return few rows off a full page. Compare cardsOnPage to totalItems.',
    },
    {
        name: 'sort',
        type: 'best_match | newly_listed | ending_soonest | price_low | price_high',
        default: 'newly_listed',
        description: 'eBay sort order for the results page.',
    },
    {
        name: 'condition',
        type: 'string',
        description: 'Comma-separated: new, open_box, refurbished, used, for_parts. Omit for any condition.',
    },
    {
        name: 'listingType',
        type: 'all | auction | buy_it_now',
        default: 'all',
        description: 'Listing format filter.',
    },
    {
        name: 'minPrice',
        type: 'number',
        description: 'Minimum price in the marketplace currency.',
    },
    {
        name: 'maxPrice',
        type: 'number',
        description: 'Maximum price in the marketplace currency.',
    },
];

export type FieldDoc = {
    name: string;
    type: string;
    description: string;
};

export const SEARCH_RESPONSE_FIELDS: FieldDoc[] = [
    { name: 'keyword', type: 'string', description: 'Echo of the request keyword.' },
    { name: 'page', type: 'number', description: 'Page you asked for.' },
    { name: 'sold', type: 'boolean', description: 'Whether this was a sold or active search.' },
    { name: 'country', type: 'US | UK', description: 'Marketplace pool used.' },
    {
        name: 'itemLocation',
        type: 'country | available | worldwide',
        description: 'Seller-location filter applied.',
    },
    {
        name: 'currency',
        type: 'string',
        description: 'Expected ISO 4217 for the country pool. Rows were filtered against it.',
    },
    {
        name: 'totalItems',
        type: 'number',
        description: 'Rows in items after filters. Not a marketplace grand total. eBay does not publish one.',
    },
    {
        name: 'cardsOnPage',
        type: 'number',
        description:
            'Cards eBay served before daysToScrape, currency filter, and count. Difference from totalItems is what filtering removed.',
    },
    {
        name: 'hasNextPage',
        type: 'boolean',
        description: 'Whether another page is available. Your job to increment page.',
    },
    {
        name: 'windowStart',
        type: 'string | null',
        description: 'Oldest sold date kept when daysToScrape > 0, else null.',
    },
    { name: 'items', type: 'Listing[]', description: 'The rows for this page.' },
    { name: 'fetchedAt', type: 'string', description: 'ISO instant when the page was scraped.' },
];

export const LISTING_FIELDS: FieldDoc[] = [
    { name: 'itemId', type: 'string | null', description: 'eBay item id when present.' },
    { name: 'title', type: 'string', description: 'Listing title.' },
    { name: 'url', type: 'string | null', description: 'Listing URL.' },
    {
        name: 'soldPrice',
        type: 'number | null',
        description: 'Realized price on sold rows (low bound of a range when applicable).',
    },
    {
        name: 'soldPriceMax',
        type: 'number | null',
        description: 'High bound of a sold price range, when eBay showed one.',
    },
    { name: 'soldDate', type: 'string | null', description: 'When it sold, sold rows only.' },
    {
        name: 'bestOfferAccepted',
        type: 'boolean',
        description: 'Accepted Best Offer. Realized price may still be unknown. Never assumed to equal the ask.',
    },
    { name: 'price', type: 'number | null', description: 'Asking price on active rows.' },
    { name: 'priceMax', type: 'number | null', description: 'High bound of an active price range.' },
    { name: 'currency', type: 'string | null', description: 'ISO currency on the row when known.' },
    {
        name: 'shipping',
        type: 'number | null',
        description: 'Shipping amount when known. Null means unknown, not free.',
    },
    {
        name: 'shippingType',
        type: 'free | paid | unknown | null',
        description: 'How shipping was labeled. Prefer this over guessing from a zero shipping field.',
    },
    { name: 'totalPrice', type: 'number | null', description: 'Item + shipping when both are known.' },
    { name: 'condition', type: 'string | null', description: 'Human-readable condition.' },
    { name: 'listingType', type: 'string | null', description: 'eBay listing format label.' },
    {
        name: 'buyingFormat',
        type: 'auction | buyItNow | auctionWithBIN | null',
        description: 'Normalized buying format when parsed.',
    },
    { name: 'bidsCount', type: 'number | null', description: 'Bid count when shown.' },
    { name: 'location', type: 'string | null', description: 'Item location string from eBay.' },
    { name: 'watcherCount', type: 'number | null', description: 'Active listings only, when shown.' },
    { name: 'unitsSold', type: 'number | null', description: 'Active multi-qty sold count when shown.' },
    { name: 'timeLeft', type: 'string | null', description: 'Auction time left when shown.' },
];

export type ErrorDoc = {
    status: number;
    meaning: string;
    when: string;
};

export const PUBLIC_ERRORS: ErrorDoc[] = [
    {
        status: 400,
        meaning: 'Bad request',
        when: 'Invalid or missing query parameters.',
    },
    {
        status: 401,
        meaning: 'Unauthorized',
        when: 'Missing, malformed, or revoked API key.',
    },
    {
        status: 403,
        meaning: 'Quota exhausted',
        when: 'Monthly request entitlement is gone. Not a rate limit. Waiting does not help.',
    },
    {
        status: 429,
        meaning: 'Rate limited',
        when: 'More than 60 requests in a minute. Retry-After is set; waiting fixes it.',
    },
    {
        status: 502,
        meaning: 'Upstream blocked',
        when: 'eBay returned a block or non-result page. Retryable. Never treated as an empty market.',
    },
    {
        status: 503,
        meaning: 'Unavailable',
        when: 'Scraper unreachable, timed out, or global budget trip. Retryable.',
    },
];
